import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

import { EMPTY_X_REPORT_DATA, type XReportData } from "@/lib/xReport";

/**
 * The X-Report slice — the original's module **`51303`**.
 *
 * ```js
 * let u = { data: { [TOTAL_SALES]: [], … 14 keys … }, loading: !1,
 *           lastTenantId: "", lastFetchedTime: s.A9 },
 * ```
 *
 * Two things about it are load-bearing rather than incidental:
 *
 * 1. **`data` is an enum-keyed object of arrays, never null.** The failure path
 *    is `rejectWithValue({ data: u.data })` — an empty report, not an absent
 *    one — because the transform indexes straight into all thirteen keys it
 *    reads and would throw on a missing one. So a failed fetch renders "No
 *    Data", it does not blank the drawer.
 * 2. **`lastFetchedTime` is a cache, and the Refresh button is what defeats
 *    it.** The original refetches only when `refetch` is set, or the tenant
 *    changed, or the entry is older than its TTL. The drawer's Refresh passes
 *    `refetch: true`, which is the whole reason that button exists.
 *
 * The POS's `commonFilters` half of that condition (`lastUpdated > lastFetchedTime`)
 * has no counterpart here: the storefront has no filter bar, so there is
 * nothing that can invalidate the cache except the tenant and the button.
 */

/** `s.J7` — the TTL the cached report is considered fresh for. */
export const X_REPORT_TTL_MS = 60_000;

export interface XReportState {
  data: XReportData;
  loading: boolean;
  lastTenantId: string;
  lastFetchedTime: number;
}

const initialState: XReportState = {
  data: EMPTY_X_REPORT_DATA(),
  loading: false,
  lastTenantId: "",
  // `s.A9` — the "never fetched" sentinel, which must be older than any TTL.
  lastFetchedTime: 0,
};

const xReportSlice = createSlice({
  name: "x-report",
  initialState,
  reducers: {
    setXReportLoading(state, action: PayloadAction<boolean>) {
      state.loading = Boolean(action.payload);
    },
    setXReport(
      state,
      action: PayloadAction<{ data: XReportData; tenantId: string }>,
    ) {
      state.data = action.payload.data;
      state.lastTenantId = action.payload.tenantId;
      state.lastFetchedTime = Date.now();
      state.loading = false;
    },
    /** `c.rejected` — the empty shape, never a null. */
    resetXReport() {
      return initialState;
    },
  },
});

export const { setXReport, setXReportLoading, resetXReport } =
  xReportSlice.actions;
export default xReportSlice.reducer;
